import type {GlobalAgentsMenuWithAnchorProps} from '@github-ui/global-agents-menu/GlobalAgentsMenu'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {AgentIcon, TriangleDownIcon} from '@primer/octicons-react'
import {Button, IconButton, Spinner} from '@primer/react'
import {lazy, type MouseEventHandler, Suspense, useCallback, useRef, useState} from 'react'

import {useDeferredPayloadSlice} from '../utils/DeferredNavPayloadContext'

export type LazyGlobalNavAgentsMenuProps = Pick<
  GlobalAgentsMenuWithAnchorProps,
  'anchorProps' | 'copilotApiUrl' | 'isOneCopilotNav'
>

export function LazyGlobalNavAgentsMenu({anchorProps, copilotApiUrl, isOneCopilotNav}: LazyGlobalNavAgentsMenuProps) {
  const {isBrowser} = useRuntimeEnvironment()
  const [shouldLoad, setShouldLoad] = useState(true)
  const [openOnLoad, setOpenOnLoad] = useState(false)
  const anchorRef = useRef<HTMLButtonElement | null>(null)
  const {data: agentsPanelProps, isPending} = useDeferredPayloadSlice('agents_menu')

  const buttonId = agentsPanelProps?.agentsButtonId ?? 'global-copilot-agent-button'

  const handleIntentToOpen = useCallback(() => {
    setShouldLoad(true)
  }, [])

  const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    event => {
      setShouldLoad(true)
      setOpenOnLoad(true)
      anchorProps?.onClick?.(event)
    },
    [anchorProps],
  )

  if (!isBrowser || !shouldLoad) {
    return (
      <AgentsButtonFallback
        anchorProps={anchorProps}
        loading={false}
        openOnLoad={false}
        handleIntentToOpen={handleIntentToOpen}
        handleClick={handleClick}
        buttonId={buttonId}
        isOneCopilotNav={isOneCopilotNav}
      />
    )
  }

  return (
    <Suspense
      fallback={
        <AgentsButtonFallback
          anchorProps={anchorProps}
          loading={isPending}
          openOnLoad={openOnLoad}
          handleIntentToOpen={handleIntentToOpen}
          handleClick={handleClick}
          buttonId={buttonId}
          isOneCopilotNav={isOneCopilotNav}
        />
      }
    >
      <LazyGlobalAgentsMenuWithAnchor
        {...agentsPanelProps}
        copilotApiUrl={copilotApiUrl}
        isOneCopilotNav={isOneCopilotNav}
        open={openOnLoad && !isPending}
        setOpen={setOpenOnLoad}
        anchorRef={anchorRef}
        anchorProps={{
          ...anchorProps,
          id: buttonId,
          onClick: event => {
            anchorProps?.onClick?.(event)
          },
        }}
      />
    </Suspense>
  )
}

function AgentsButtonFallback({
  loading,
  openOnLoad,
  handleIntentToOpen,
  handleClick,
  buttonId,
  anchorProps,
  isOneCopilotNav,
}: {
  loading: boolean
  openOnLoad?: boolean
  handleIntentToOpen: () => void
  handleClick: MouseEventHandler<HTMLButtonElement>
  buttonId: string
  anchorProps?: GlobalAgentsMenuWithAnchorProps['anchorProps']
  isOneCopilotNav?: boolean
}) {
  // only show the spinner if we're waiting to show the dialog
  const shouldShowLoading = loading && openOnLoad
  if (isOneCopilotNav) {
    return (
      <IconButton
        id={buttonId}
        variant={anchorProps?.variant ?? 'invisible'}
        className={anchorProps?.className}
        icon={shouldShowLoading ? () => <Spinner size="small" /> : TriangleDownIcon}
        aria-label="Open Copilot menu"
        aria-haspopup="dialog"
        onMouseEnter={handleIntentToOpen}
        onFocus={handleIntentToOpen}
        onClick={handleClick}
      />
    )
  }

  return (
    <Button
      id={buttonId}
      variant={anchorProps?.variant ?? 'invisible'}
      className={anchorProps?.className}
      leadingVisual={AgentIcon}
      trailingVisual={shouldShowLoading ? () => <Spinner size="small" /> : TriangleDownIcon}
      aria-label="Open agents panel"
      onMouseEnter={handleIntentToOpen}
      onFocus={handleIntentToOpen}
      onClick={handleClick}
    >
      {''}
    </Button>
  )
}

const LazyGlobalAgentsMenuWithAnchor = lazy(async () => {
  const mod = await import('@github-ui/global-agents-menu/GlobalAgentsMenu')
  return {default: mod.GlobalAgentsMenuWithAnchor}
})
